import { logUsage } from 'owa-analytics';
import { SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME, SPAN_TAG_NAME } from 'owa-compose-link';
import { AnchorElementsSource } from 'owa-link-data';
import { getFileProviderType } from 'owa-fileprovider-link';
import { trace } from 'owa-trace';
import LinkPlugin from '../plugin/LinkPlugin';
import { LinkIdContainer } from '../schema/LinkIdContainer';
import { SharingLink } from '../schema/SharingLink';
import { isBeautifulLinkBasedOnId } from './beautifiedPrefix';
import { getSharingInfoToTryUpdateLink } from './handleOneLink/getSharingInfoToTryUpdateLink';
import shouldProcessLink from './shouldProcessLink';
import trySetIdAndClassName from './trySetIdAndClassName';
import { isSupportedProviderType } from './isSupportedProviderType';

export default async function processAnchorElementsFromHtmlContent(
    allLinks: NodeListOf<Element> | Element[],
    hiddenReferenceAttachments: NodeListOf<Element> | Element[],
    source: AnchorElementsSource,
    linkPlugin: LinkPlugin,
    linkIdContainer: LinkIdContainer,
    linkIdsWithSpinner: string[],
    shouldChangeLinkId: boolean,
    shouldTryBeautify: boolean
) {
    // We need to set id and className for all sharePoint links so that getAllSharingLinks can find them.
    const sharingLinks: SharingLink[] = [];
    for (let i = 0; i < allLinks.length; i++) {
        const link = allLinks[i] as HTMLAnchorElement;
        if (shouldProcessLink(link, hiddenReferenceAttachments)) {
            const url = getUrl(link);

            const fileProviderType = getFileProviderType(url);
            if (!isSupportedProviderType(fileProviderType)) {
                continue;
            }

            trySetIdAndClassName(link, shouldChangeLinkId);
            sharingLinks.push({
                id: link.id,
                url: link.href,
                anchorElement: link,
                providerType: fileProviderType,
            });
        }
    }

    if (sharingLinks.length < 10) {
        logUsage('UnderTenSPLinksWhenProcessAnchorElements', [
            source === AnchorElementsSource.PasteHtml,
            sharingLinks.length,
        ]);
    } else {
        logUsage('AtLeastTenSPLinksWhenProcessAnchorElements', [
            source === AnchorElementsSource.PasteHtml,
        ]);
    }

    trace.info('process anchor elements total count: ' + sharingLinks.length);
    for (let i = 0; i < sharingLinks.length; i++) {
        const sharingLink = sharingLinks[i];
        const isLinkPreviouslyBeautified: boolean = isBeautifulLinkBasedOnId(
            sharingLink.anchorElement
        );
        const isLinkPureAnchorElement: boolean = isPureAnchorElement(sharingLink.anchorElement);
        const isLinkWithOnlyOneSpanChild: boolean = isAnchorElementWithOnlyOneSpanChild(
            sharingLink.anchorElement
        );
        const shouldBeautify: boolean =
            isEligibleForBeautifyLink(sharingLink.anchorElement) &&
            isLinkPureAnchorElement &&
            shouldTryBeautify; // We only try to beautify for some links from paste

        const shouldRehydrate: boolean =
            isLinkPreviouslyBeautified || isLinkPureAnchorElement || isLinkWithOnlyOneSpanChild;
        logUsageForLinks(
            source,
            isLinkPreviouslyBeautified,
            isLinkPureAnchorElement,
            shouldBeautify,
            isLinkWithOnlyOneSpanChild
        );
        getSharingInfoToTryUpdateLink(
            linkPlugin,
            sharingLink.providerType,
            shouldBeautify,
            shouldRehydrate,
            isLinkPreviouslyBeautified,
            sharingLink.anchorElement,
            linkIdContainer,
            i,
            linkIdsWithSpinner,
            source
        );
    }
}

function logUsageForLinks(
    source: AnchorElementsSource,
    isLinkPreviouslyBeautified: boolean,
    isLinkPureAnchorElement: boolean,
    shouldBeautify: boolean,
    isLinkWithOnlyOneSpanChild: boolean
) {
    let linkType: string = '';
    if (shouldBeautify) {
        linkType = 'shouldBeautify';
    } else if (isLinkPreviouslyBeautified) {
        linkType = 'isLinkPreviouslyBeautified';
    } else if (isLinkPureAnchorElement) {
        linkType = 'isLinkPureAnchorElement';
    } else if (isLinkWithOnlyOneSpanChild) {
        linkType = 'isLinkWithOnlyOneSpanChild';
    }
    if (source === AnchorElementsSource.PasteHtml) {
        logUsage('ComposeSPLinksFromPaste', [linkType]);
    } else {
        logUsage('ComposeSPLinksFromDraft', [linkType]);
    }
}

export function getUrl(link: HTMLAnchorElement): string {
    const url: string = link.getAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME);
    if (url && url.length > 0) {
        return url;
    }

    return link.href;
}

export function isEligibleForBeautifyLink(link: HTMLAnchorElement): boolean {
    let url: string = link.getAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME);
    if (!url) {
        url = link.href;
    }

    return url.toLowerCase() === link.text.toLowerCase();
}

export function isPureAnchorElement(link: HTMLAnchorElement): boolean {
    return !link.children || link.children.length === 0;
}

function isAnchorElementWithOnlyOneSpanChild(link: HTMLAnchorElement): boolean {
    return (
        link.children &&
        link.children.length === 1 &&
        link.children[0].tagName.toUpperCase() === SPAN_TAG_NAME &&
        (!link.children[0].children || link.children[0].children.length === 0)
    );
}
